import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Vec3 } from '@qoobd/rules';
import { game, PLAYER, ROOM_SIZE, roomCenter } from '../game/controller';
import { useGame } from '../game/store';

const MAX_SEALS = 4;

/** Rotate a group whose "front" is +Z so it faces along a world axis. */
function faceAxis(obj: THREE.Object3D, dir: Vec3): void {
  obj.rotation.set(0, 0, 0);
  if (dir[0] !== 0) obj.rotation.y = Math.PI / 2;
  else if (dir[1] !== 0) obj.rotation.x = Math.PI / 2;
}

/** Red bars across doors sealed by a bulkhead, drawn only where the player can see. */
export function Seals({ all = false }: { all?: boolean }) {
  const groups = useRef<(THREE.Group | null)[]>([]);
  const material = useMemo(() => new THREE.MeshStandardMaterial({ color: 0xe5484d, emissive: 0xe5484d, emissiveIntensity: 1.3, metalness: 0.4, roughness: 0.4 }), []);
  const bar = useMemo(() => new THREE.BoxGeometry(1.9, 0.14, 0.1), []);
  useFrame(() => {
    const m = game.match;
    if (!m) return;
    // Spectators see every seal in the rooms on show; players only the ones in their own view.
    const seals = all ? m.allSeals().filter((s) => game.visible.has(s.from) || game.visible.has(s.to)) : m.viewFor(PLAYER).seals;
    for (let i = 0; i < MAX_SEALS; i++) {
      const g = groups.current[i];
      if (!g) continue;
      const seal = seals[i];
      g.visible = !!seal;
      if (!seal) continue;
      const a = roomCenter(m.maze, seal.from);
      const b = roomCenter(m.maze, seal.to);
      g.position.copy(a).lerp(b, 0.5);
      faceAxis(g, m.maze.directionBetween(seal.from, seal.to));
    }
  });
  return (
    <>
      {Array.from({ length: MAX_SEALS }, (_, i) => (
        <group key={i} ref={(el) => (groups.current[i] = el)} visible={false}>
          {[-0.55, 0, 0.55].map((y) => (
            <mesh key={y} geometry={bar} material={material} position={[0, y, 0]} />
          ))}
          {[-0.55, 0, 0.55].map((x) => (
            <mesh key={`v${x}`} geometry={bar} material={material} position={[x, 0, 0]} rotation={[0, 0, Math.PI / 2]} scale={[0.85, 1, 1]} />
          ))}
        </group>
      ))}
    </>
  );
}

/** Supply crates in visible rooms. They sit off-centre, so the player never stands inside one. */
export function Caches({ all = false }: { all?: boolean }) {
  const version = useGame((s) => s.sceneVersion);
  const caches = useMemo(() => {
    const m = game.match;
    const list = all ? m?.allCaches().filter((c) => game.visible.has(c.room)) : m?.viewFor(PLAYER).caches;
    return m && list ? list.map((c) => ({ ...c, pos: roomCenter(m.maze, c.room).add(new THREE.Vector3(1.05, -1.05, 1.05)) })) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
  const m = useMemo(
    () => ({
      crate: new THREE.MeshStandardMaterial({ color: 0x3a3f47, metalness: 0.6, roughness: 0.45 }),
      strip: new THREE.MeshStandardMaterial({ color: 0xe8e3d9, emissive: 0xe8e3d9, emissiveIntensity: 1.4 }),
    }),
    [],
  );
  return (
    <>
      {caches.map((c) => (
        <group key={c.room} position={c.pos}>
          <mesh material={m.crate}>
            <boxGeometry args={[0.6, 0.6, 0.6]} />
          </mesh>
          <mesh material={m.strip} position={[0, 0, 0]}>
            <boxGeometry args={[0.64, 0.06, 0.64]} />
          </mesh>
        </group>
      ))}
    </>
  );
}

/**
 * The rival's thruster scorch in the player's own room: a fading heat ring, plus a streak
 * toward the door they left by while it's fresh. Neutral colour: it never says who left it.
 */
export function ScorchMark() {
  const ring = useRef<THREE.Mesh>(null);
  const streak = useRef<THREE.Mesh>(null);
  const ringMat = useMemo(() => new THREE.MeshBasicMaterial({ color: 0xd9c7a3, transparent: true, opacity: 0, depthWrite: false }), []);
  const streakMat = useMemo(() => new THREE.MeshBasicMaterial({ color: 0xffd9a0, transparent: true, opacity: 0, depthWrite: false }), []);
  useFrame(({ clock }) => {
    const m = game.match;
    if (!m || !ring.current || !streak.current) return;
    const view = m.viewFor(PLAYER);
    const mark = view.scorch;
    ring.current.visible = !!mark;
    streak.current.visible = !!mark?.exit;
    if (!mark) return;
    const center = roomCenter(m.maze, view.self.room);
    const fade = Math.max(0, 1 - mark.ageMs / 30000);
    const flicker = 0.85 + 0.15 * Math.sin(clock.elapsedTime * 9);
    ring.current.position.copy(center);
    faceAxis(ring.current, mark.exit ?? view.self.orientation.up);
    ringMat.opacity = 0.35 * fade * flicker;
    if (mark.exit) {
      const e = mark.exit;
      streak.current.position.copy(center).add(new THREE.Vector3(e[0], e[1], e[2]).multiplyScalar(ROOM_SIZE * 0.3));
      faceAxis(streak.current, e);
      streakMat.opacity = 0.6 * Math.max(0, 1 - mark.ageMs / 5000) * flicker;
    }
  });
  return (
    <>
      <mesh ref={ring} material={ringMat} visible={false}>
        <torusGeometry args={[1.45, 0.05, 8, 48]} />
      </mesh>
      {/* a thin glowing streak along +Z, rotated to point at the exit door */}
      <mesh ref={streak} material={streakMat} visible={false}>
        <boxGeometry args={[0.06, 0.06, 1.1]} />
      </mesh>
    </>
  );
}
